import { pdf } from '@react-pdf/renderer';
import { message } from 'antd';
import { useState } from 'react';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { resolveSignatureUrl } from 'api/signatures';
import { getImagesVoucher } from 'api/attentionVoucher';
import { getStaticVoucherData } from 'api/budget';
import VoucherDocument from './VoucherDocument/VoucherDocument';
import { TemplateRenderer } from 'PDF/TemplateEngine';
import { resolveDocDesign } from 'PDF/TemplateEngine/resolveDocDesign';
import { getPdfTemplateByTypeApi } from 'api/pdfTemplate';

/**
 * Hook que renderiza el comprobante como PDF y lo envía al backend
 * para su archivado inmutable y distribución por email.
 *
 * Sigue el mismo patrón de carga de datos que ActionsComprobantesProvider y
 * el mismo criterio de selección lowcode/legacy que Dowload.js: se resuelve
 * `design_version` del comprobante contra la plantilla del tenant para decidir
 * qué componente renderizar — garantizando que el PDF archivado sea idéntico
 * al que el usuario descarga en pantalla.
 */
const useVoucherSend = () => {
  const [loading, setLoading] = useState(false);

  /**
   * @param {{ id: number }} voucher  - objeto de la fila (solo necesita .id)
   * @param {{ to: string[], cc: string[] }|null} recipients - null dispara modo
   *   automático (`auto: true`, sin destinatarios): el backend re-verifica el
   *   flag del cliente y deriva el destinatario desde `Cliente.email`.
   */
  const sendVoucher = async (voucher, recipients) => {
    setLoading(true);
    // recipients == null → modo automático (envío de sistema, sin toasts).
    // Declarado fuera del try para poder silenciar también el toast de error.
    const auto = recipients == null;
    try {
      const token = getAccessTokenApi();

      // Obtener datos completos del comprobante, config de tenant, imágenes
      // y plantilla lowcode en paralelo.
      const [voucherDetail, staticData, imagesData, templateRaw] = await Promise.all([
        axios
          .get(`${API_PATH}/get-attention-vouchers/${voucher.id}`, {
            headers: { Authorization: token },
          })
          .then((r) => r.data),
        getStaticVoucherData(token),
        getImagesVoucher(voucher.id),
        getPdfTemplateByTypeApi(null, 'comprobante'),
      ]);

      const config = staticData?.tenant || {};
      const images = imagesData?.images || [];

      // Normalizar respuesta del template (igual que useTemplateOrFallback):
      // si hubo error de fetch, el api wrapper devuelve { status: 'client', ... }.
      const template = templateRaw?.status === 'client' ? null : templateRaw || null;

      // Resolver URLs de firma desde el detalle completo
      const [spec, recep] = await Promise.all([
        resolveSignatureUrl(voucherDetail?.specialist_signature),
        resolveSignatureUrl(voucherDetail?.recepcionist_signature),
      ]);

      const voucherWithSignatures = {
        ...voucherDetail,
        specialist_signature: spec,
        recepcionist_signature: recep,
      };

      // Mismo criterio de selección de diseño que Dowload.js
      const design = resolveDocDesign(voucherDetail, template);

      let doc;
      if (design === 'lowcode') {
        const hallazgos = (voucherDetail?.hallazgos || []).filter((h) => h.mostrar_en_comprobante);
        const data = {
          ...voucherWithSignatures,
          images,
          hallazgos,
          hallazgos_count: hallazgos.length,
        };
        doc = <TemplateRenderer template={template} data={data} config={config || {}} />;
      } else {
        doc = <VoucherDocument voucher={voucherWithSignatures} config={config} images={images} />;
      }

      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();

      const fd = new FormData();
      fd.append('file', blob, `Comprobante N°${voucher.id}.pdf`);
      fd.append('to', JSON.stringify(recipients?.to || []));
      fd.append('cc', JSON.stringify(recipients?.cc || []));
      if (auto) fd.append('auto', 'true');

      await axios.post(`${API_PATH}/comprobantes/${voucher.id}/send`, fd, {
        headers: { Authorization: token },
      });

      // En modo automático (auto) el envío es una acción de sistema en segundo
      // plano: NO mostramos toast al técnico (que solo creó el comprobante).
      if (!auto) message.success('Comprobante enviado');
    } catch (e) {
      // Detalle técnico a consola → Sentry front; al usuario, mensaje simple.
      console.error(
        '[send-comprobante] fallo al enviar:',
        e?.response?.status,
        e?.response?.data || e
      );
      // Silencioso en modo auto: el caller (autoSendVoucherIfEnabled) ya
      // absorbe el throw y el comprobante ya quedó creado.
      if (!auto) message.error('No se pudo enviar el comprobante. Intenta nuevamente.');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { sendVoucher, loading };
};

export default useVoucherSend;
